import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Heart, Loader2, Save, Smile, Battery, Brain, Moon, Scale, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import WelfareTrends from '../components/welfare/WelfareTrends';
import WelfareReflections from '../components/welfare/WelfareReflections';
import WelfareSettings from '../components/welfare/WelfareSettings';
import WelfareAlertsBanner from '../components/welfare/WelfareAlertsBanner';
import WelfareResources from '../components/welfare/WelfareResources';
import WelfareChat from '../components/welfare/WelfareChat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRICS = [
  { key: 'mood', label: 'Mood', icon: Smile, low: 'Low', high: 'Great', color: 'rose' },
  { key: 'energy', label: 'Energy', icon: Battery, low: 'Drained', high: 'Energised', color: 'amber' },
  { key: 'stress', label: 'Stress', icon: Brain, low: 'Calm', high: 'Stressed', color: 'red', inverse: true },
  { key: 'sleep', label: 'Sleep Quality', icon: Moon, low: 'Poor', high: 'Restful', color: 'indigo' },
  { key: 'work_life', label: 'Work-Life Balance', icon: Scale, low: 'Off', high: 'Balanced', color: 'emerald' },
];

const DEFAULT_VALUES = { mood: 5, energy: 5, stress: 5, sleep: 5, work_life: 5 };

export default function Welfare() {
  const [values, setValues] = useState(DEFAULT_VALUES);
  const [note, setNote] = useState('');
  const [todayEntry, setTodayEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('today');
  const [aiOptIn, setAiOptIn] = useState(false);

  useEffect(() => {
    loadToday();
    loadSettingsSnapshot();
  }, []);

  useEffect(() => {
    if (activeTab === 'chat') {
      loadSettingsSnapshot();
    }
  }, [activeTab]);

  const loadSettingsSnapshot = async () => {
    try {
      const res = await axios.get(`${API}/welfare/settings`);
      setAiOptIn(!!res.data?.ai_chat_opt_in);
    } catch (e) {
      // Non-critical — settings snapshot just controls the AI opt-in UI toggle.
      // Surface to console so Sentry captures it once DSN is wired up.
      console.error('[Welfare] loadSettingsSnapshot failed:', e);
    }
  };

  useEffect(() => {
    loadToday();
  }, []);

  const loadToday = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/welfare/check-in/today`);
      if (res.data) {
        setTodayEntry(res.data);
        setValues({
          mood: res.data.mood,
          energy: res.data.energy,
          stress: res.data.stress,
          sleep: res.data.sleep,
          work_life: res.data.work_life,
        });
        setNote(res.data.note || '');
      }
    } catch (error) {
      console.error('Failed to load welfare entry:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSlider = (key, val) => {
    setValues(prev => ({ ...prev, [key]: val[0] }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await axios.post(`${API}/welfare/check-in`, {
        ...values,
        note: note.trim() || null,
      });
      setTodayEntry(res.data);
      toast.success(todayEntry ? 'Check-in updated' : 'Check-in saved');
    } catch (error) {
      console.error('Failed to save check-in:', error);
      toast.error(error.response?.data?.detail || 'Failed to save check-in');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 page-slide-in" data-testid="welfare-container">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight flex items-center gap-3">
              <Heart className="w-8 h-8 text-rose-500" strokeWidth={1.5} />
              Coach Welfare
            </h1>
            <p className="text-gray-500 mt-1">A private space to check in with yourself. Only you can see this.</p>
          </div>
          {todayEntry && (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5" data-testid="welfare-checked-in-badge">
              <CheckCircle2 className="w-4 h-4" />
              <span>Checked in today</span>
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-6 gap-1 h-auto" data-testid="welfare-tabs">
            <TabsTrigger value="today" data-testid="welfare-tab-today">Today</TabsTrigger>
            <TabsTrigger value="trends" data-testid="welfare-tab-trends">Trends</TabsTrigger>
            <TabsTrigger value="reflections" data-testid="welfare-tab-reflections">Reflections</TabsTrigger>
            <TabsTrigger value="resources" data-testid="welfare-tab-resources">Resources</TabsTrigger>
            <TabsTrigger value="chat" data-testid="welfare-tab-chat">AI Chat</TabsTrigger>
            <TabsTrigger value="settings" data-testid="welfare-tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="today">
            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
              </div>
            ) : (
              <div className="space-y-4">
                <WelfareAlertsBanner />
                <Card data-testid="welfare-checkin-card">
                <CardHeader>
                  <CardTitle className="text-xl">How are you doing today?</CardTitle>
                  <p className="text-sm text-gray-500">
                    Slide each scale from 1 to 10. Takes less than 15 seconds. Save when you're done.
                  </p>
                </CardHeader>
                <CardContent className="space-y-8">
                  {METRICS.map(metric => {
                    const Icon = metric.icon;
                    const value = values[metric.key];
                    return (
                      <div key={metric.key} data-testid={`welfare-metric-${metric.key}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-5 h-5 text-${metric.color}-500`} strokeWidth={1.5} />
                            <span className="font-medium text-gray-900">{metric.label}</span>
                          </div>
                          <span className={`text-2xl font-bold text-${metric.color}-600 tabular-nums`} data-testid={`welfare-value-${metric.key}`}>
                            {value}
                          </span>
                        </div>
                        <Slider
                          min={1}
                          max={10}
                          step={1}
                          value={[value]}
                          onValueChange={(v) => handleSlider(metric.key, v)}
                          data-testid={`welfare-slider-${metric.key}`}
                        />
                        <div className="flex justify-between text-xs text-gray-400 mt-1">
                          <span>{metric.low}</span>
                          <span>{metric.high}</span>
                        </div>
                      </div>
                    );
                  })}

                  <div>
                    <label className="font-medium text-gray-900 block mb-2">
                      Anything on your mind? <span className="text-gray-400 text-sm font-normal">(optional)</span>
                    </label>
                    <Textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="A quick note for yourself — no one else can see this."
                      rows={3}
                      maxLength={500}
                      data-testid="welfare-note-textarea"
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">{note.length}/500</p>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="bg-rose-500 hover:bg-rose-600 text-white"
                      data-testid="welfare-save-btn"
                    >
                      {saving ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" />{todayEntry ? 'Update Check-in' : 'Save Check-in'}</>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="trends">
            <WelfareTrends />
          </TabsContent>
          <TabsContent value="reflections">
            <WelfareReflections />
          </TabsContent>
          <TabsContent value="resources">
            <WelfareResources />
          </TabsContent>
          <TabsContent value="chat">
            <WelfareChat
              optInEnabled={aiOptIn}
              onRequestEnable={() => setActiveTab('settings')}
            />
          </TabsContent>
          <TabsContent value="settings">
            <WelfareSettings />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
